import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { LineItem } from '../models/shared/LineItem';
import { Table, FormControl, Dropdown } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { AdvancedButton } from './AdvancedButton';
import { useText } from '../context/TextContext';
import { ThreeDotsMenu } from './ThreeDotsMenu';
import { AdvancedDropdownItem } from './AdvancedDropdownItem';
import ArticleSearch from './ArticleSearch';
import { Article } from '../models/shared/Article';
import { sortArticles } from '../utils/shared/article';
import { useFirebaseQueryOptions } from '../hooks/firebaseHooks';
import { COLLECTIONS, DISCOUNT_LINE_ITEM_ID } from '../utils/shared/constants';
import { useOrganization } from '../context/OrganizationContext';
import { where } from 'firebase/firestore';
import { calculatePriceWithoutVat, calculatePriceWithVat, formatPrice, formatCurrency, calculateLineItemsTotal } from '../utils/shared/priceCalculator';

function NumberInput({ value, onChange, ...props }: { value: number; onChange: (n: number) => void; [key: string]: unknown }) {
    const [local, setLocal] = useState(String(value));
    useEffect(() => { setLocal(String(value)); }, [value]);
    return (
        <FormControl
            {...props}
            type="number"
            value={local}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setLocal(e.target.value);
                const n = Number(e.target.value);
                if (e.target.value !== '' && !isNaN(n)) onChange(n);
            }}
            onBlur={() => {
                const n = Number(local);
                if (local === '' || isNaN(n)) setLocal(String(value));
            }}
        />
    );
}

interface Props {
    lineItems: LineItem[];
    onLineItemsChange: (lineItems: LineItem[]) => void;
    readOnly?: boolean;
}

export interface LineItemsEditorHandle {
    addLineItem: () => void;
    addDiscountItem: () => void;
}

/**
 * Shared line-item table + totals editor, used by both the Offer line-items
 * block and the Invoice editor/public view. Exposes addLineItem/addDiscountItem
 * via ref so callers (e.g. the Offer block toolbar) can trigger them from
 * outside the always-visible "Add row"/"Add discount" buttons it renders itself.
 */
const LineItemsEditor = forwardRef<LineItemsEditorHandle, Props>(({ lineItems, onLineItemsChange, readOnly }, ref) => {
    const { orgId, organization } = useOrganization();
    const currency = organization.settings?.currency ?? 'SEK';
    const [newLineItemId, setNewLineItemId] = useState<string>('');
    const [unsortedArticles] = useFirebaseQueryOptions<Article>(`${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`, {
        queryConstraints: [where('status', '==', 'active')],
        skip: readOnly,
    });
    const articles = useMemo(() => sortArticles(unsortedArticles), [unsortedArticles]);
    const text = useText();

    const addLineItem = () => {
        const newItem: LineItem = {
            id: uuidv4(),
            description: '',
            quantity: 1,
            unitPrice: 0,
            vatRate: 0,
        };

        onLineItemsChange([...lineItems, newItem]);
        setNewLineItemId(newItem.id);
    };

    const addDiscountItem = () => {
        const newItem: LineItem = {
            id: DISCOUNT_LINE_ITEM_ID,
            articleId: DISCOUNT_LINE_ITEM_ID,
            description: text.discount,
            quantity: 1,
            unitPrice: -100,
            vatRate: 0,
        };

        onLineItemsChange([...lineItems, newItem]);
        setNewLineItemId(newItem.id);
    };

    useImperativeHandle(ref, () => ({ addLineItem, addDiscountItem }));

    const updateLineItem = (id: string, changes: Partial<LineItem>) => {
        onLineItemsChange(lineItems.map(item =>
            item.id === id ? { ...item, ...changes } : item
        ));
    };

    const updateLineItemWithArticle = (id: string, article: Article) => {
        onLineItemsChange(lineItems.map(item => {
            if (item.id === id) {
                const newLineItem: LineItem = {
                    ...item,
                    articleId: article.id,
                    description: article.name,
                    unitPrice: article.perUnitPrice,
                    vatRate: article.vatRate,
                };
                if (article.taxBenefit) {
                    newLineItem.taxBenefit = article.taxBenefit
                }
                return newLineItem;
            }
            return item;
        }));
    };

    const removeLineItem = (id: string) => {
        onLineItemsChange(lineItems.filter(item => item.id !== id));
    };

    const moveLineItemUp = (index: number) => {
        if (index <= 0) return;

        const updatedItems = [...lineItems];
        const temp = updatedItems[index];
        updatedItems[index] = updatedItems[index - 1];
        updatedItems[index - 1] = temp;

        onLineItemsChange(updatedItems);
    };

    const moveLineItemDown = (index: number) => {
        if (index >= lineItems.length - 1) return;

        const updatedItems = [...lineItems];
        const temp = updatedItems[index];
        updatedItems[index] = updatedItems[index + 1];
        updatedItems[index + 1] = temp;

        onLineItemsChange(updatedItems);
    };

    const sums = calculateLineItemsTotal(lineItems);
    const lineItemsWithoutDiscount = lineItems.filter(item => item.id !== DISCOUNT_LINE_ITEM_ID);
    const discountItems = lineItems.filter(item => item.id === DISCOUNT_LINE_ITEM_ID);
    const orderedLineItems = [...lineItemsWithoutDiscount, ...discountItems];

    return (
        <div className="d-flex flex-column gap-2">
            {!readOnly && (
                <div className="d-flex gap-2">
                    <AdvancedButton variant="clear" size="sm" onClick={addLineItem} icon='bi-plus'>
                        {text.addRow}
                    </AdvancedButton>
                    <AdvancedButton variant="clear" size="sm" onClick={addDiscountItem} icon='bi-plus'>
                        {text.addDiscount}
                    </AdvancedButton>
                </div>
            )}

            <div className="table-responsive-sm m-0">
                <Table responsive="sm" className='bg-offer offer-lines table-borderless'>
                    <thead>
                        <tr>
                            {!readOnly && <th>{text.article}</th>}
                            <th>{text.description}</th>
                            <th>{text.priceWithVat}</th>
                            <th className='text-center'>{text.quantity}</th>
                            <th className='text-end'>{text.total}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderedLineItems.map((item, index) => {
                            const isDiscount = item.id === DISCOUNT_LINE_ITEM_ID;
                            const lineItemCalculations = sums.items[index];
                            const currentArticle = articles.find(article => article.id === item.articleId);
                            let placeHolder = text.searchArticle;
                            if (currentArticle) {
                                placeHolder = `${currentArticle.name} (${text.vat}: ${currentArticle.vatRate}%)`;
                            }
                            const priceWithVat = calculatePriceWithVat(item.unitPrice, item.vatRate);

                            return (
                                <tr key={item.id}>
                                    {!readOnly && (
                                        <td style={{ verticalAlign: 'middle' }}>
                                            {isDiscount
                                                ? <div className='text-muted small h-100'>{'<' + text.discount + '>'}</div>
                                                : <ArticleSearch
                                                    articles={articles}
                                                    onArticleSelect={(article) => updateLineItemWithArticle(item.id, article)}
                                                    placeholder={placeHolder}
                                                    autoFocus={item.id === newLineItemId}
                                                />
                                            }
                                        </td>
                                    )}
                                    <td>
                                        <FormControl
                                            type="text"
                                            value={item.description}
                                            onChange={(e) => updateLineItem(item.id, { description: e.target.value })}
                                            size="sm"
                                            disabled={!item.articleId}
                                            readOnly={readOnly}
                                        />
                                    </td>
                                    <td>
                                        {readOnly
                                            ? <FormControl type="text" value={formatPrice(priceWithVat)} readOnly size="sm" className="text-end" style={{ minWidth: "100px" }} />
                                            : <NumberInput
                                                value={priceWithVat}
                                                onChange={(n) => updateLineItem(item.id, { unitPrice: calculatePriceWithoutVat(n, item.vatRate) })}
                                                step="0.01"
                                                size="sm"
                                                disabled={!item.articleId}
                                                className="text-end"
                                                style={{ minWidth: "100px" }}
                                            />
                                        }
                                    </td>
                                    <td>
                                        {readOnly
                                            ? <FormControl type="text" value={item.quantity} readOnly size="sm" className="text-end" style={{ minWidth: "70px", maxWidth: "90px" }} />
                                            : <NumberInput
                                                value={item.quantity}
                                                onChange={(n) => updateLineItem(item.id, { quantity: n })}
                                                min="1"
                                                size="sm"
                                                disabled={!item.articleId}
                                                className="text-end"
                                                style={{ minWidth: "70px", maxWidth: "90px" }}
                                            />
                                        }
                                    </td>

                                    <td className="text-end">
                                        <div className="d-flex justify-content-end align-items-center">
                                            <FormControl
                                                value={formatPrice(lineItemCalculations.totalWithVatBeforeDiscount)}
                                                readOnly
                                                size="sm"
                                                className="text-end"
                                                style={{ minWidth: "120px" }}
                                            />
                                            {!readOnly && <ThreeDotsMenu>
                                                <AdvancedDropdownItem
                                                    icon="bi-arrow-up"
                                                    text={text.moveUp}
                                                    onClick={() => moveLineItemUp(index)}
                                                    disabled={index === 0 || isDiscount}
                                                />
                                                <AdvancedDropdownItem
                                                    icon="bi-arrow-down"
                                                    text={text.moveDown}
                                                    onClick={() => moveLineItemDown(index)}
                                                    disabled={index === lineItemsWithoutDiscount.length - 1 || isDiscount}
                                                />
                                                <Dropdown.Divider />
                                                <AdvancedDropdownItem
                                                    icon="bi-trash"
                                                    text={text.deleteRow}
                                                    onClick={() => removeLineItem(item.id)}
                                                />
                                            </ThreeDotsMenu>}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                <div className='bg-dark text-white rounded p-3 mt-2'>
                    <div className="row mb-2">
                        <div className="col-8 text-end">{text.total}:</div>
                        <div className="col-4 text-end">{formatCurrency(sums.totalWithVat, currency)}</div>
                    </div>
                    {sums.vatBreakdown.map(vatItem => (
                        <div className="row mb-2" key={`vat-${vatItem.rate}`}>
                            <div className="col-8 text-end">{text.vat} {vatItem.rate}%:</div>
                            <div className="col-4 text-end">{formatCurrency(vatItem.vatAmount, currency)}</div>
                        </div>
                    ))}
                    <div className="row mb-2">
                        <div className="col-8 text-end">{text.excludingVat}:</div>
                        <div className="col-4 text-end">{formatCurrency(sums.totalBeforeVat, currency)}</div>
                    </div>

                    {sums.totalRutSubsidyAmount > 0 && (
                        <div className="row mb-2">
                            <div className="col-8 text-end">{text.rutSubsidyAmount}:</div>
                            <div className="col-4 text-end">-{formatCurrency(sums.totalRutSubsidyAmount, currency)}</div>
                        </div>
                    )}
                    <div className="row mt-3">
                        <div className="col-8 text-end fw-bold fs-5">{text.toPay}:</div>
                        <div className="col-4 text-end fw-bold text-accent fs-5">{formatCurrency(sums.totalToPay, currency)}</div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default LineItemsEditor;
